// Light / dark theme runtime. Mirrors the website's src/lib/theme.js.
// Keep the key, the values and the meta colours in sync with
// app/views/layouts/_theme_boot.html.erb, which applies the theme before first paint.

export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'codalio-theme';
export const THEME_EVENT = 'codalio-theme';
// The theme while the toggle flag is off: dark until light mode has been through QA
// (plan decision D1). With the flag on, a visitor with no stored choice gets
// systemTheme() instead.
export const DEFAULT_THEME: Theme = 'dark';
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';
export const THEME_COLOR: Record<Theme, string> = {
  light: '#F5F5F5',
  dark: '#06070B'
};

const isTheme = (value: unknown): value is Theme =>
  value === 'light' || value === 'dark';

// The toggle ships behind a flag until every screen works in light (D9). While it is
// off, nobody can get stuck in an unfinished light mode from an old stored value.
// The flag is FeatureFlag::THEME_TOGGLE, which the Rails layout renders onto
// <html data-theme-toggle>: the head script reads the same attribute, so the two always
// agree, and it is there on the signed-out pages without a request.
export function isThemeToggleEnabled(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute('data-theme-toggle') === 'true';
}

// Development only: `?theme=light` forces a theme without the flag, for working on
// light mode before it ships.
function readDevOverride(): Theme | null {
  if (!import.meta.env.DEV || typeof window === 'undefined') return null;
  try {
    const value = new URLSearchParams(window.location.search).get('theme');
    return isTheme(value) ? value : null;
  } catch {
    return null;
  }
}

// The OS / browser colour scheme. Light when the browser cannot tell us.
export function systemTheme(): Theme {
  try {
    return window.matchMedia(SYSTEM_DARK_QUERY).matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

// A choice made while storage is blocked lasts for this page only. It is kept here so
// a later system change does not replace it.
let unsavedTheme: Theme | null = null;

export function readStoredTheme(): Theme {
  const override = readDevOverride();
  if (override) return override;
  if (!isThemeToggleEnabled()) return DEFAULT_THEME;
  if (unsavedTheme) return unsavedTheme;
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(value) ? value : systemTheme();
  } catch {
    return systemTheme();
  }
}

// The theme currently on <html>. Every consumer reads this rather than React state so
// they all agree with the head script.
export function currentTheme(): Theme {
  if (typeof document === 'undefined') return DEFAULT_THEME;
  const value = document.documentElement.getAttribute('data-bs-theme');
  return isTheme(value) ? value : DEFAULT_THEME;
}

// Idempotent, and leaves every other class on <html> alone.
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (root.getAttribute('data-bs-theme') !== theme) {
    root.setAttribute('data-bs-theme', theme);
  }
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[theme]);
  // Rhino's ThemedLogo (on its auth pages) reads data-bs-theme from <html> but only
  // watches <body> for changes; touching an attribute here is what wakes it up.
  document.body?.setAttribute('data-codalio-theme', theme);
}

export function setTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    unsavedTheme = null;
  } catch {
    // Private mode or blocked storage: the choice lasts for this page only.
    unsavedTheme = theme;
  }
  applyTheme(theme);
  window.dispatchEvent(new CustomEvent<Theme>(THEME_EVENT, { detail: theme }));
}

// Fires on a change in this tab, on a change made in another tab, and on a change to
// the system colour scheme (which only matters while no choice is stored). A null key
// is localStorage.clear() in another tab, which may have dropped the stored theme too.
export function subscribeTheme(callback: (theme: Theme) => void): () => void {
  const onLocal = (event: Event) => {
    const { detail } = event as CustomEvent<Theme>;
    callback(isTheme(detail) ? detail : currentTheme());
  };
  const reread = () => {
    const theme = readStoredTheme();
    applyTheme(theme);
    callback(theme);
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== THEME_STORAGE_KEY) return;
    reread();
  };
  let systemQuery: MediaQueryList | null = null;
  try {
    systemQuery = window.matchMedia(SYSTEM_DARK_QUERY);
  } catch {
    // No matchMedia: the system theme never changes under us.
  }
  window.addEventListener(THEME_EVENT, onLocal);
  window.addEventListener('storage', onStorage);
  systemQuery?.addEventListener?.('change', reread);
  return () => {
    window.removeEventListener(THEME_EVENT, onLocal);
    window.removeEventListener('storage', onStorage);
    systemQuery?.removeEventListener?.('change', reread);
  };
}

// Called once from each entrypoint: a safety net behind the head script, plus
// cross-tab sync.
export function initTheme(): void {
  applyTheme(readStoredTheme());
  subscribeTheme(applyTheme);
}
